import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { SearchIcon, FlagIcon } from './Icons';
import Pagination from './Pagination';
import OrderQueryForm, { QUERY_TYPE_OPTIONS, STATUS_BADGE_CLASS } from './OrderQueryForm';
import OrderQueryDetail from './OrderQueryDetail';

const DEFAULT_PAGE_SIZE = 50;

// Backend caps list-all mode at 500 (see crm_integration's
// ORDER_QUERY_LIST_MAX_LIMIT) - well above real order-query volume, so one
// fetch at this limit is effectively "all of them" for both the stat-card
// counts and the bucketed table below.
const LIST_ALL_LIMIT = 500;

const OPEN_STATUSES = ['open', 'reopened'];
const FINAL_STATUSES = ['resolved', 'rejected'];

// Mirrors the ERP frontend's own bucketing (see OrderQueryPage.tsx's
// queryBucket) so the CRM's stat cards/table groupings read identically to
// the ERP's - keep the two in sync if either changes. `pending` here is the
// `pending` field from the list response, i.e. a reply from the ERP side the
// CRM hasn't seen yet (see _is_pending_for_crm on the ERP). `status ===
// 'new'` is a separate signal: the query hasn't been claimed by anyone on
// the ERP side yet (see the ERP's OrderQueryViewSet.open action) - it also
// lands in Pending even though nobody has replied.
export function queryBucket(q) {
  if (FINAL_STATUSES.includes(q.status)) return 'done';
  if (q.status === 'new') return 'pending';
  if (q.pending) return 'pending';
  if (q.status === 'marked_done') return 'approval_pending';
  if (OPEN_STATUSES.includes(q.status)) return 'open';
  return 'done';
}

const STAT_CARDS = [
  { value: 'all', label: 'All', className: '' },
  { value: 'pending', label: 'Pending', className: 'pending' },
  { value: 'open', label: 'Open', className: 'open' },
  { value: 'approval_pending', label: 'Approval Pending', className: 'approval-pending' },
  { value: 'done', label: 'Solved', className: 'done' },
];

function formatDate(raw) {
  if (!raw) return '-';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Browsable list of every ERP order query, mirroring the ERP's own
// order-queries admin view (stat-card buckets, newest first) - see
// server/ivr/erpClient.js's fetchOrderQueries "list all" mode, used here
// whenever the order number/phone fields are empty. Typing into either
// field and hitting Search switches to the ERP's narrower order/phone
// lookup instead (which the ERP doesn't apply a bucket filter to, so
// picking a stat card clears the search fields and vice versa - the two
// modes are mutually exclusive on the ERP side, not stackable). Always a
// live fetch; nothing from the ERP is ever stored here, same principle as
// the Daily Orders page / incoming-call popup.
function OrderQueries() {
  const { authFetch } = useAuth();
  const [orderNumber, setOrderNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [bucketFilter, setBucketFilter] = useState('all');
  // "My Queries" scopes list-all mode to queries this agent raised - server
  // resolves the identity from the session (see GET /erp/order-queries),
  // never trusted from here. Mutually exclusive with the order/phone search
  // fields, same as bucketFilter (the ERP's list-all mode is what supports
  // both filters; its order/phone search mode supports neither).
  const [mineOnly, setMineOnly] = useState(false);
  // Every query in the current mine-scope, unfiltered by bucket - the
  // source for both the stat-card counts and (client-side bucketed) the
  // table, mirroring the ERP page's own fully-client-side model instead of
  // asking the server for one raw status at a time (the server's `status`
  // filter can't express "Open" as open+reopened or "Pending" as
  // unread-regardless-of-status anyway).
  const [allResults, setAllResults] = useState([]);
  // Non-null while an order-number/phone search is the active view; that
  // mode bypasses buckets entirely, same as before.
  const [searchRows, setSearchRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showForm, setShowForm] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const fetchAll = async (mine = mineOnly) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('limit', String(LIST_ALL_LIMIT));
      if (mine) params.set('mine', 'true');
      const response = await authFetch(`/api/ivr/erp/order-queries?${params.toString()}`);
      const body = await response.json();
      if (!response.ok) throw new Error(body?.error || 'Failed to fetch order queries');
      setAllResults(Array.isArray(body?.results) ? body.results : []);
    } catch (err) {
      setAllResults([]);
      setError(err.message || 'Failed to fetch order queries');
    } finally {
      setLoading(false);
    }
  };

  const fetchSearch = async (orderNumberValue, phoneValue) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (orderNumberValue) params.set('order_number', orderNumberValue);
      if (phoneValue) params.set('phone', phoneValue);
      const response = await authFetch(`/api/ivr/erp/order-queries?${params.toString()}`);
      const body = await response.json();
      if (!response.ok) throw new Error(body?.error || 'Failed to fetch order queries');
      setSearchRows(Array.isArray(body?.results) ? body.results : []);
    } catch (err) {
      setSearchRows([]);
      setError(err.message || 'Failed to fetch order queries');
    } finally {
      setLoading(false);
    }
  };

  // Re-runs whichever view is currently active - used after a mutation
  // (raise/mark-done/approve/reject/reopen) so both the table and the stat
  // counts stay in sync with what just changed.
  const refresh = () => {
    const trimmedOrder = orderNumber.trim();
    const trimmedPhone = phone.trim();
    if (trimmedOrder || trimmedPhone) {
      fetchSearch(trimmedOrder, trimmedPhone);
    } else {
      fetchAll();
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const bucketCounts = useMemo(() => {
    const counts = { all: allResults.length, pending: 0, open: 0, approval_pending: 0, done: 0 };
    for (const q of allResults) counts[queryBucket(q)] += 1;
    return counts;
  }, [allResults]);

  const displayedRows = useMemo(() => {
    if (searchRows !== null) return searchRows;
    if (bucketFilter === 'all') return allResults;
    return allResults.filter((q) => queryBucket(q) === bucketFilter);
  }, [searchRows, allResults, bucketFilter]);

  useEffect(() => {
    setPage(1);
  }, [displayedRows.length]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setBucketFilter('all');
    setMineOnly(false);
    const trimmedOrder = orderNumber.trim();
    const trimmedPhone = phone.trim();
    if (trimmedOrder || trimmedPhone) {
      fetchSearch(trimmedOrder, trimmedPhone);
    } else {
      setSearchRows(null);
      fetchAll(false);
    }
  };

  const handleBucketCard = (value) => {
    setBucketFilter(value);
    setOrderNumber('');
    setPhone('');
    setSearchRows(null);
  };

  const handleMineToggle = (value) => {
    setMineOnly(value);
    setOrderNumber('');
    setPhone('');
    setSearchRows(null);
    fetchAll(value);
  };

  const totalPages = Math.max(1, Math.ceil(displayedRows.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const pageRows = displayedRows.slice((page - 1) * pageSize, page * pageSize);

  return (
    <>
      <form className="incoming-filters-bar" onSubmit={handleSearchSubmit}>
        <div className="search-input-wrap">
          <SearchIcon className="search-input-icon" width={15} height={15} />
          <input
            type="text"
            className="search-input"
            placeholder="Order number"
            value={orderNumber}
            onChange={(event) => setOrderNumber(event.target.value)}
            aria-label="Search by order number"
          />
        </div>
        <div className="search-input-wrap">
          <SearchIcon className="search-input-icon" width={15} height={15} />
          <input
            type="text"
            className="search-input"
            placeholder="Phone number"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            aria-label="Search by phone number"
          />
        </div>
        <button type="submit" className="btn btn-secondary" disabled={loading}>
          {loading ? 'Searching…' : 'Search'}
        </button>
        <button type="button" className="btn btn-primary" onClick={() => setShowForm(true)}>
          <FlagIcon width={14} height={14} />
          Raise Query
        </button>
      </form>

      {/* Stat cards double as the bucket filter - click one to filter the
          table below, same idiom as the stat cards on Incoming Calls, but
          the count on each card tells you where the backlog sits before you
          click anything (mirrors the ERP's own Order Queries page). */}
      <div className="stat-cards" style={{ marginBottom: '1.25rem' }}>
        {STAT_CARDS.map((card) => (
          <button
            key={card.value}
            type="button"
            className={`stat-card ${card.className} ${bucketFilter === card.value && searchRows === null ? 'active' : ''}`}
            onClick={() => handleBucketCard(card.value)}
          >
            <div className="stat-value">{bucketCounts[card.value]}</div>
            <div className="stat-label">{card.label}</div>
          </button>
        ))}
      </div>

      <div className="customer-toggle" style={{ marginBottom: '1.25rem' }} role="group" aria-label="Filter by who raised the query">
        <button type="button" className={!mineOnly && searchRows === null ? 'active' : ''} onClick={() => handleMineToggle(false)}>
          All Queries
        </button>
        <button type="button" className={mineOnly && searchRows === null ? 'active' : ''} onClick={() => handleMineToggle(true)}>
          My Queries
        </button>
      </div>

      {error && <div className="call-feedback call-feedback-error">{error}</div>}

      {loading ? (
        <div className="loading-state">Loading…</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order Number</th>
                <th>Customer Name</th>
                <th>Mobile</th>
                <th>Query Type</th>
                <th>Status</th>
                <th>Query</th>
                <th>Agent</th>
                <th>Raised</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center' }}>
                    No order queries found.
                  </td>
                </tr>
              ) : (
                pageRows.map((item) => {
                  const typeLabel =
                    item.query_type_label ||
                    QUERY_TYPE_OPTIONS.find((o) => o.value === item.query_type)?.label ||
                    item.query_type;
                  return (
                    <tr
                      key={item.id}
                      className={`table-row-clickable ${item.pending ? 'row-pending' : ''}`}
                      onClick={() => setDetailId(item.id)}
                    >
                      <td className="table-cell-strong">{item.order_number || '-'}</td>
                      <td>{item.customer_name || '-'}</td>
                      <td>{item.customer_mobile || '-'}</td>
                      <td>{typeLabel || '-'}</td>
                      <td>
                        <span className={STATUS_BADGE_CLASS[item.status] || 'status'}>
                          {item.status_label || item.status || '-'}
                        </span>
                        {item.pending && <span className="status attempted status-dot order-query-pending-badge">Pending</span>}
                      </td>
                      <td className="table-cell-truncate" title={item.query || ''}>
                        {item.query || '-'}
                      </td>
                      <td>{item.agent_name || '-'}</td>
                      <td>{formatDate(item.created_at)}</td>
                      <td>{formatDate(item.updated_at)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        totalItems={displayedRows.length}
      />

      {showForm && (
        <OrderQueryForm
          orderNumber={orderNumber.trim()}
          customerMobile={phone.trim()}
          onClose={() => {
            setShowForm(false);
            refresh();
          }}
        />
      )}

      {detailId && (
        <OrderQueryDetail
          id={detailId}
          onClose={() => {
            setDetailId(null);
            refresh();
          }}
        />
      )}
    </>
  );
}

export default OrderQueries;
